import { useLoaderData, useSearchParams } from 'react-router';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import type { loader as articlesLoader } from '@modules/professional/page/loaders/articles-page-loader.server';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import {
    IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    IN_PAGE_SEARCH_PARAM_KEY,
} from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useTabLoadMore } from '@modules/professional/hook/use-tab-load-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import {
    detailCta,
    DetailTileMedia,
    detailTileActionsFor,
} from '~/ui-rocco/pages/detail-shared';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { ProfessionalHeadingCtas } from './ProfessionalHeadingCtas';
import { DiscoverHeader } from '~/ui-rocco/pages/home-page/components/DiscoverHeader';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { formatSectionNumber } from '../utils/section-numbering';
import { getArticleGridLayout } from '../utils/article-grid-layout';
import { PAGE_TITLE_TYPE, TAB_CONTENT_GAP } from '../utils/tab-spacing';

const ARTICLES_DESCRIPTION =
    'Get informed and make the best decisions for your project. We bring you insights and interviews from those at the forefront of architecture and construction.';

/**
 * §8 — (04) Articles. Mirrors the logged-out homepage ArticlesSection exactly:
 * the shared DiscoverHeader (popular categories + description + CTA) above a
 * magazine grid — one large feature card beside a 2×2 of smaller cards — with
 * Gestura Headline (`font-gestura`) serif titles. Self-hides when empty.
 */
interface ArticleCardProps {
    id?: number;
    image?: string;
    title?: string;
    category?: string;
    date?: string;
    readTime?: string;
    link?: string;
    size: 'large' | 'small';
    /** Extra classes for the tile root — e.g. `md:h-full` to fill a stretched grid cell. */
    className?: string;
    /** Extra classes for the media box, applied after its default aspect ratio. */
    mediaClassName?: string;
}

const ArticleCard = ({
    id,
    image,
    title,
    category,
    date,
    readTime,
    link,
    size,
    className,
    mediaClassName,
}: ArticleCardProps) => {
    if (!title) return null;
    const meta = [date, readTime].filter(Boolean).join(', ');
    const actions = detailTileActionsFor({
        id: id ?? 0,
        link: link ?? '/articles',
        title,
        images: image ? [{ src: image }] : [],
        pinType: id ? 'ArticlePin' : undefined,
    });
    return (
        <div className={cn('relative', className)}>
            <RoccoLink
                to={link ?? '/articles'}
                className="flex h-full flex-col"
            >
                <DetailTileMedia
                    src={image}
                    alt={title}
                    className={cn(
                        'bg-neutral-200',
                        size === 'large'
                            ? 'aspect-[855/597]'
                            : 'aspect-[398/489]',
                        mediaClassName,
                    )}
                    imgClassName="max-w-none"
                    pinId={id}
                    pinType={id ? 'ArticlePin' : undefined}
                    enableActionPanel
                    enquiry={actions.enquiry}
                    share={actions.share}
                    tileInfo={actions.tileInfo}
                />
                <p className="m-0 font-gestura leading-[1.14] font-normal tracking-[-0.01em] text-[#1f1f21] capitalize max-md:pt-3 max-md:text-base md:pt-[1.248rem] md:text-[1.7496rem]">
                    {title}
                </p>
                {category && (
                    <p className="m-0 font-sans font-medium tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-2 max-md:text-xs md:pt-[0.996rem] md:text-[0.75rem]">
                        {category}
                    </p>
                )}
                {meta && (
                    <p className="m-0 font-sans font-normal tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-0.5 max-md:text-xs md:pt-[0.252rem] md:text-[0.75rem]">
                        {meta}
                    </p>
                )}
            </RoccoLink>
            {/* Mobile actions live outside the tile link so a tap on the `•••`
            can never navigate. */}
            {id ? (
                <TileMobileActionsSlot
                    pinId={id}
                    pinType="ArticlePin"
                    enableActionPanel
                    enquiry={actions.enquiry}
                    share={actions.share}
                    tileInfo={actions.tileInfo}
                />
            ) : null}
        </div>
    );
};

interface ProfessionalArticlesSectionProps {
    /**
     * `overview` (default): the numbered "(0N — Articles)" Discover header used
     * on the professional overview page. `tab`: the standalone Articles-tab
     * header — a big "Articles" title + intro, then an "(All Articles)" divider.
     */
    variant?: 'overview' | 'tab';
    /** Overview-only: this section's position in the page's "(0N)" sequence. */
    sectionNumber?: number;
}

/**
 * Tab-only search + sort bar (prod parity: the journal handle sets
 * `allowFilters: false`, so {@link SearchFilter} renders the search pill + sort
 * dropdown without a category sidebar). Kept as its own component so its hooks
 * only mount on the Articles tab, leaving the overview variant untouched.
 */
const ArticlesTabFilterBar = () => {
    const { filterStates } = useLoaderData<typeof articlesLoader>();
    const { Title } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();
    const category = useMatchesHandleData<SearchIndex>('category', 'blogpost');
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    });

    return (
        <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] md:px-[3.132rem] md:pt-[2.4rem]">
            <SearchFilter
                searchFilters={searchFilters}
                filterDrawerOpen={filterDrawerOpen}
                toggleFilterDrawer={toggleFilterDrawer}
                searchBarVariant="revamp"
                searchPlaceholder={`Search Article by ${Title}`}
            />
            {/* The applied-filter bar is desktop-only; on mobile the Refine
                button's badge + sheet already surface these. */}
            {desktop &&
                searchFilters.appliedFilters &&
                searchFilters.appliedFilters.length > 0 && (
                    <ProfessionalAppliedFilters
                        filterItems={searchFilters.appliedFilters}
                        onClearAll={() =>
                            searchFilters.resetFilters(
                                resetFilterStates(
                                    searchFilters.filterStates,
                                    IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
                                ),
                            )
                        }
                    />
                )}
        </div>
    );
};

export const ProfessionalArticlesSection = ({
    variant = 'overview',
    sectionNumber,
}: ProfessionalArticlesSectionProps = {}) => {
    const { articleTiles, articlesHasMore, professionalOverview } =
        useLoaderData<typeof loader>();
    const { URLSegment } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const [searchParams] = useSearchParams();
    const isTab = variant === 'tab';
    const hasActiveSearch = !!searchParams.get(IN_PAGE_SEARCH_PARAM_KEY);

    // Infinite scroll powers the standalone Articles tab; the overview variant
    // keeps its fixed 5-card magazine layout, so it never appends pages.
    const {
        items: loadedArticles,
        hasMore,
        isLoading,
        loadMore,
        pageStartByIndex,
    } = useTabLoadMore({
        initialItems: articleTiles ?? [],
        initialHasMore: isTab && articlesHasMore,
        initialPage: 1,
        urlSegment: URLSegment,
        searchType: 'blogpost',
        getKey: (article) => article.id,
    });

    // `?page=N` follows the page of articles actually in view, scrolling
    // either direction — not the pages the infinite-scroll lookahead fetched.
    useViewportPageSync({
        enabled: isTab && (hasMore || Object.keys(pageStartByIndex).length > 1),
    });

    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    // Overview self-hides when the pro has no articles. The tab keeps its
    // header + search bar only for a 0-result *search* (so it can be cleared);
    // with no search and nothing to show it self-hides too, mirroring the
    // Network tab — otherwise a pro with no journal content would see a
    // misleading "try changing the search term".
    if (loadedArticles.length === 0 && !(isTab && hasActiveSearch)) return null;
    // Magazine hero = 1 feature + its portrait tiles; the tab spills the rest
    // into a uniform continuation grid that infinite scroll keeps growing.
    const { heroCount, isWideHero } = getArticleGridLayout(
        loadedArticles.length,
        isTab,
    );
    const [feature, ...rest] = loadedArticles.slice(0, heroCount);
    const continuation = isTab ? loadedArticles.slice(heroCount) : [];

    const articlesLink = `${professionalOverview.Link}/journal`;
    // Popular categories = the professional's own article categories. Each
    // tile's label is a comma-joined list, so split + flatten + dedupe.
    const categories: CategoryPill[] = Array.from(
        new Set(
            loadedArticles
                .flatMap((article) => (article.label ?? '').split(','))
                .map((label) => label.trim())
                .filter(Boolean),
        ),
    )
        .slice(0, 6)
        .map((label) => ({ label, to: articlesLink }));

    return (
        <section className="w-full overflow-hidden bg-white text-[#1f1f21]">
            {isTab ? (
                // Desktop keeps the editorial title + intro + "(All Articles)"
                // divider; on mobile the tab goes straight to the search + grid.
                desktop && (
                    <>
                        <div
                            className={cn(
                                'mx-auto w-full max-w-[120rem] md:px-[3.132rem]',
                                TAB_CONTENT_GAP,
                            )}
                        >
                            <div className="flex md:items-start md:justify-between">
                                <h2
                                    className={cn(
                                        'm-0 font-sans font-normal text-[#141414]',
                                        PAGE_TITLE_TYPE,
                                    )}
                                >
                                    Articles
                                </h2>
                                <ProfessionalHeadingCtas />
                            </div>
                            <p className="m-0 font-sans leading-[1.25] font-normal text-[#1f1f21] md:mt-[5.9375rem] md:max-w-[60.375rem] md:text-[2.0625rem]">
                                {ARTICLES_DESCRIPTION}
                            </p>
                        </div>
                        <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[10rem]">
                            <div className="h-px w-full bg-black/20" />
                            <SectionLabel className="mt-[1.062rem] block text-[#181818] md:text-[2rem]">
                                (All Articles)
                            </SectionLabel>
                        </div>
                    </>
                )
            ) : (
                <DiscoverHeader
                    theme="light"
                    fullWidthRule
                    unit="rem"
                    flushTop
                    stackOnMobile
                    eyebrow={`(${formatSectionNumber(sectionNumber ?? 4)} — Articles)`}
                    heading="Read about us"
                    categories={categories}
                    description={ARTICLES_DESCRIPTION}
                    ctaLabel="View all Articles"
                    ctaTo={articlesLink}
                    ctaClassName={detailCta(
                        'filled-light',
                        'w-fit max-md:h-[2.75rem] max-md:px-6 max-md:text-sm',
                    )}
                    categoriesClassName="md:gap-[1.5625rem]"
                    // Match the sibling detail-page section headers (Team /
                    // Network) — 200px in rem, sized and tracked as they are.
                    headingClassName="font-sans max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]"
                />
            )}

            {isTab && <ArticlesTabFilterBar />}

            {loadedArticles.length === 0 ? (
                <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] max-md:pb-14 md:px-[3.132rem] md:pt-[2.4rem] md:pb-[7.5rem]">
                    <NoSearchResults resultsType="articles" />
                </div>
            ) : (
                <div
                    className={cn(
                        'mx-auto grid w-full max-w-[120rem] grid-cols-2 max-md:gap-x-2 max-md:gap-y-6 max-md:px-6 max-md:pt-[2.4rem] md:grid-cols-12 md:gap-x-[3.6875rem] md:gap-y-[5.9375rem] md:px-[3.132rem]',
                        isTab ? 'md:pt-[1.5rem]' : 'md:pt-[10rem]',
                    )}
                >
                    {feature && (
                        <div
                            className={cn(
                                'max-md:col-span-2 md:col-span-6',
                                isWideHero ? 'md:h-full' : 'md:row-span-2',
                            )}
                            // The feature opens page 1 — the viewport page
                            // sync needs a lowest-page marker to clear
                            // `?page=N` when the reader scrolls back up.
                            // Tab-only: the overview variant never pages.
                            {...(isTab ? viewportPageMarkerProps(1) : {})}
                        >
                            <ArticleCard
                                id={feature.id}
                                image={feature.image}
                                title={feature.title}
                                category={feature.label}
                                date={feature.date}
                                readTime={feature.readTime}
                                link={feature.link}
                                size="large"
                                // The feature's landscape art is taller than the
                                // portrait tiles beside it, so in the wide hero
                                // its media fills the row instead of setting its
                                // own height — the row is sized by those tiles,
                                // so both bottom out level at every width.
                                className={cn(isWideHero && 'md:h-full')}
                                mediaClassName={cn(
                                    isWideHero &&
                                        'md:aspect-auto md:min-h-0 md:flex-1',
                                )}
                            />
                        </div>
                    )}
                    {rest.map((article, index) => (
                        <div
                            key={article.id ?? index}
                            className="md:col-span-3"
                        >
                            <ArticleCard
                                id={article.id}
                                image={article.image}
                                title={article.title}
                                category={article.label}
                                date={article.date}
                                readTime={article.readTime}
                                link={article.link}
                                size="small"
                            />
                        </div>
                    ))}
                </div>
            )}

            {continuation.length > 0 && (
                <div className="mx-auto grid w-full max-w-[120rem] grid-cols-2 max-md:gap-x-2 max-md:gap-y-6 max-md:px-6 max-md:pt-6 md:grid-cols-4 md:gap-x-[3.6875rem] md:gap-y-[5.9375rem] md:px-[3.132rem] md:pt-[10rem]">
                    {continuation.map((article, index) => {
                        const startPage = pageStartByIndex[heroCount + index];
                        return (
                            <div
                                key={article.id ?? index}
                                {...(startPage === undefined
                                    ? {}
                                    : viewportPageMarkerProps(startPage))}
                            >
                                <ArticleCard
                                    id={article.id}
                                    image={article.image}
                                    title={article.title}
                                    category={article.label}
                                    date={article.date}
                                    readTime={article.readTime}
                                    link={article.link}
                                    size="small"
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {isTab && infiniteEnabled && hasMore && (
                <div
                    ref={sentinelRef}
                    aria-hidden="true"
                    style={{ height: 1 }}
                />
            )}
            {isTab && showViewMore && (
                <div className="flex justify-center pt-[3rem] pb-[3.6rem]">
                    <button
                        type="button"
                        onClick={onViewMore}
                        className={detailCta(
                            'outlined-light',
                            'max-md:h-[3.125rem] max-md:text-sm',
                        )}
                    >
                        View more
                    </button>
                </div>
            )}
            {isTab && isLoading && (
                <div className="flex justify-center pt-[3rem] pb-[3.6rem]">
                    <Spinner className="size-8 text-[#181818]" />
                </div>
            )}
        </section>
    );
};
